import {Textarea, Button, ButtonGroup, Chips, Choice, Dialog, Label} from 'platform/components';
import {GridItem, Grid, Show, Space, Right, Text} from 'platform/foundation';
import {v4 as uuidV4} from 'uuid';

import {useEffect, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {isEmpty, isNil} from 'ramda';
import {compact, isNotNil} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useThunkDispatch} from '../../../hooks/useThunkDispatch';
import {deleteAuditAssets} from '../../../store/carAudit/actions';
import {addAssets, deleteAssets} from '../../../store/carAudit/reducer';
import {selectAuditAssets} from '../../../store/carAudit/selectors';
import {selectUserSelectedLanguage} from '../../../store/user/selectors';
import {AuditCategoryOfStructure} from '../../../types/AuditCategoryOfStructure';
import {AuditDataAssetsFilesBody} from '../../../types/AuditDataAssetsFilesBody';
import {AuditParamDefinition} from '../../../types/AuditParamDefinition';
import {AuditDamageValueBody, TConditionForm} from '../../../types/ConditionTypes';
import {useFormRenderer} from '../../FinalForm/hooks/useFormRenderer';
import {useVehicleCreateContext} from '../../VehicleCreateContext/hooks/useVehicleCreateContext';
import {useConditionDamagesContext} from '../hooks/useConditionDamagesContext';
import {useMoveDamage} from '../hooks/useMoveDamage';
import {AuditParamType} from '../types/AuditParamType';
import {getFormFieldName} from '../utils/getFormFieldName';
import {getParamFromCategory} from '../utils/getParamFromCategory';
import {getParsedValue} from '../utils/getParsedValue';
import {getTranslation} from '../utils/getTranslation';
import {supportsDamageType} from '../utils/supportsDamageType';
import {CardImageUpload, CardImageUploadProps} from './CardImageUpload';

type AddAndEditDamageProps = {
  isOpen: boolean;
  onClose: () => void;
  category: AuditCategoryOfStructure;
  damageId?: string | null;
} & TestIdProps;

enum ProcessAction {
  confirm = 'confirm',
  cancel = 'cancel',
  delete = 'delete',
}

// eslint-disable-next-line max-lines-per-function, max-statements -- Pre-existing violation
export const AddAndEditDamage = ({isOpen, onClose, category, ...rest}: AddAndEditDamageProps) => {
  const dispatch = useThunkDispatch();
  const locale = useSelector(selectUserSelectedLanguage);

  const {
    Field: FormField,
    form: {
      getFieldState,
      mutators: {setFieldValue},
    },
  } = useFormRenderer<TConditionForm>();
  const {lastOrderNumber} = useConditionDamagesContext();
  const {deleteConditionAsset} = useVehicleCreateContext();

  const [locationPhotoParam, detailPhotoParam] =
    category.paramDefinitions?.relatedActions?.filter(
      (param) => param.type === AuditParamType.PHOTO
    ) ?? [];

  const locationAssets = useSelector(selectAuditAssets(category.id, locationPhotoParam?.id));
  const detailAssets = useSelector(selectAuditAssets(category.id, detailPhotoParam?.id));

  const {id: vehicleId} = useParams();
  // Guards against a rapid double-click firing two concurrent confirm/move requests before the
  // disabled state from `processAction` has re-rendered the button.
  const isSubmittingRef = useRef(false);

  const [damageId, setDamageId] = useState<string>(() => rest.damageId ?? uuidV4());

  const isEdit = Boolean(rest.damageId);

  const typeOfDamageParam = getParamFromCategory(category, AuditParamType.SELECTABLE_BUTTON);
  const commentOfDamageParam = getParamFromCategory(category, AuditParamType.ENHANCED_COMMENT);

  const typeFieldName = getFormFieldName(category.id, typeOfDamageParam?.id);
  const commentFieldName = getFormFieldName(category.id, commentOfDamageParam?.id);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [processAction, setProcessAction] = useState<ProcessAction | null>(null);

  const [assetsForSave, setAssetsForSave] = useState<Record<string, AuditDataAssetsFilesBody[]>>(
    {}
  );
  const [assetsForDelete, setAssetsForDelete] = useState<Record<string, string[]>>({});

  const addAssetForSave = (paramDefinitionId: string, asset: AuditDataAssetsFilesBody) => {
    const _assetsForSave = assetsForSave;
    _assetsForSave[paramDefinitionId] = [...(assetsForSave?.[paramDefinitionId] ?? []), asset];
    setAssetsForSave(_assetsForSave);
  };

  const deleteAssetForSave = (paramDefinitionId: string, imageId: string) => {
    assetsForSave[paramDefinitionId] = assetsForSave[paramDefinitionId]?.filter(
      (asset) => asset.imageId !== imageId
    );

    setAssetsForSave(assetsForSave);
  };

  const addAssetForDelete = (paramDefinitionId: string, imageId: string) => {
    const _assetsForDelete = assetsForDelete;
    _assetsForDelete[paramDefinitionId] = [
      ...(assetsForDelete?.[paramDefinitionId] ?? []),
      imageId,
    ];
    setAssetsForDelete(_assetsForDelete);
  };

  const getDamageValue = (fieldName: string): AuditDamageValueBody[] => {
    const fieldState = getFieldState(fieldName);
    const value = fieldState?.value;

    if (!value || !Array.isArray(value)) {
      return [];
    }
    return value as AuditDamageValueBody[];
  };

  const getInitialValue = (fieldName: string) => {
    if (!isEdit) {
      return null;
    }

    const damageValue = getDamageValue(fieldName);
    // Match on `rest.damageId` (the incoming prop), not the internal `damageId` state: the reset
    // effect that seeds the type/comment runs before its own setDamageId has applied, so the
    // internal state still points at the previously edited damage — which would show that
    // damage's type/comment one dialog late when switching between damages.
    const damage = damageValue?.find((damage) => damage.damageId === rest.damageId);

    return damage?.type ?? null;
  };

  const [damageType, setDamageType] = useState<string | null>(() => getInitialValue(typeFieldName));
  const [damageComment, setDamageComment] = useState<string | null>(() =>
    getInitialValue(commentFieldName)
  );
  const [isValidated, setIsValidated] = useState(false);
  const isDamageTypeInvalid = isValidated && (isNil(damageType) || isEmpty(damageType));

  const deleteAssetsOfVehicleCreate = (imageIds?: string[]) => {
    if (!deleteConditionAsset || !imageIds) {
      return;
    }

    for (const imageId of imageIds) {
      deleteConditionAsset(imageId);
    }
  };

  const deleteAssetsForParam = async (
    paramDefinitionId: string,
    imageIds?: string[]
  ): Promise<void> => {
    if (!imageIds) {
      return;
    }

    dispatch(
      deleteAssets({
        categoryId: category.id,
        paramDefinitionId,
        imageIds,
      })
    );

    await dispatch(deleteAuditAssets.action({requestBody: {imageIds}}));
  };

  // The part the inspector picked that does not offer the currently selected damage type — set
  // while its confirmation dialog is open, cleared once they confirm or back out.
  const [pendingTargetCategoryId, setPendingTargetCategoryId] = useState<string | null>(null);

  const {
    targetCategories,
    canChangePart,
    setTargetCategoryId,
    resolvedTargetCategory,
    isPartChanged,
    moveDamage,
  } = useMoveDamage({
    category,
    isEdit,
    damageId,
    damageType,
    damageComment,
    vehicleId,
    assetsForSave,
    assetsForDelete,
    deleteAssetsForParam,
    deleteAssetsOfVehicleCreate,
    lastOrderNumber,
    onClose,
  });

  // The damage type on offer follows the *target* part, not the part the damage lives under —
  // moving it to another part means picking from what that part supports. Falls back to the
  // current part, which is also the resolved target for an in-place edit.
  const targetTypeOfDamageParam = getParamFromCategory(
    resolvedTargetCategory,
    AuditParamType.SELECTABLE_BUTTON
  );

  useEffect(
    function resetDamageState() {
      setDamageType(getInitialValue(typeFieldName));
      setDamageComment(getInitialValue(commentFieldName));
      setIsValidated(false);

      setAssetsForSave({});
      setAssetsForDelete({});
      setDamageId(rest.damageId ?? uuidV4());
      setTargetCategoryId(category.id);
      setPendingTargetCategoryId(null);
    },
    [isOpen, rest.damageId, category] // eslint-disable-line react/exhaustive-deps -- Muted pre-existing violation
  );

  const handlePartChange = (categoryId: string | Nullish) => {
    // An id that is no longer on offer resolves back to the current selection, making the change
    // a no-op rather than a silent move to a part the inspector cannot see.
    const nextCategory =
      targetCategories.find((target) => target.id === categoryId) ?? resolvedTargetCategory;

    if (supportsDamageType(nextCategory, damageType)) {
      setTargetCategoryId(nextCategory.id);
      return;
    }

    setPendingTargetCategoryId(nextCategory.id);
  };

  const handleConfirmPartChange = () => {
    if (isNil(pendingTargetCategoryId)) {
      return;
    }

    setTargetCategoryId(pendingTargetCategoryId);
    // The target does not offer this type, so it cannot come along. Dropping it re-renders the
    // chips from the target's own types with nothing selected, and `handleConfirmDamage` refuses
    // to save until the inspector picks one of those.
    setDamageType(null);
    // The empty type is this dialog's own doing, not a mistake the inspector made — so an
    // earlier failed save must not paint the chips red again the moment they confirm. The next
    // save attempt validates from scratch.
    setIsValidated(false);
    setPendingTargetCategoryId(null);
  };

  const updateDamageValue = (name: string, value: string) => {
    const damageValue = getDamageValue(name).slice();
    if (!damageValue || !Array.isArray(damageValue)) {
      return;
    }

    const damageIndex = damageValue.findIndex((damage) => damage.damageId === damageId);

    if (damageIndex < 0) {
      damageValue.push({
        damageId,
        type: value,
        order: lastOrderNumber + 1,
      });
    } else {
      damageValue[damageIndex] = {
        ...damageValue[damageIndex],
        type: value,
      };
    }

    setFieldValue(name, damageValue);
  };

  const deleteDamageValue = (fieldName: string, paramDefinitionId?: string) => {
    const damageValue = getDamageValue(fieldName);
    if (!damageValue || !paramDefinitionId) {
      return;
    }

    setFieldValue(
      fieldName,
      damageValue.filter((damage) => damage.damageId !== damageId)
    );
  };

  const addAssetsForParam = (paramDefinitionId: string, assets?: AuditDataAssetsFilesBody[]) => {
    if (!assets) {
      return;
    }

    dispatch(
      addAssets({
        categoryId: category.id,
        paramDefinitionId,
        assets,
      })
    );
  };

  const updateDamageInPlace = async (confirmedType: string): Promise<void> => {
    if (typeOfDamageParam) {
      updateDamageValue(typeFieldName, confirmedType);
    }
    if (damageComment && damageComment !== '' && commentOfDamageParam) {
      updateDamageValue(commentFieldName, damageComment);
    }

    for await (const paramDefinition of [locationPhotoParam, detailPhotoParam]) {
      if (!paramDefinition) {
        continue;
      }

      const {id: paramId} = paramDefinition;
      const imageIdsForDelete = assetsForDelete?.[paramId];

      await addAssetsForParam(paramId, assetsForSave?.[paramId]);
      await deleteAssetsForParam(paramId, imageIdsForDelete);

      deleteAssetsOfVehicleCreate(imageIdsForDelete);
    }

    onClose();
  };

  const handleConfirmDamage = async (): Promise<void> => {
    setIsValidated(true);

    if (isNil(damageType) || isEmpty(damageType) || isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setProcessAction(ProcessAction.confirm);

    if (isPartChanged) {
      await moveDamage(resolvedTargetCategory);
    } else {
      await updateDamageInPlace(damageType);
    }

    setProcessAction(null);
    isSubmittingRef.current = false;
  };

  const isNestedDialogOpen = isNotNil(pendingTargetCategoryId) || isDeleteDialogOpen;

  const handleCancelDamage = async (): Promise<void> => {
    // Guards against the Dialog's own ESC/close-icon handling calling this while a Save (move or
    // in-place) is still in flight — without this, a stale Save success handler applied after
    // cancel would re-add data the user believed they'd discarded.
    //
    // `isNestedDialogOpen` guards the same handler against ESC pressed on a nested dialog. Every
    // mounted Dialog registers its ESC listener on `window` and only checks its own `isOpen`, and
    // its `stopPropagation` does not reach listeners bound to that same target — so ESC on a
    // nested dialog reaches this dialog's `onClose` too, discarding staged uploads and closing
    // the whole editor when the inspector only meant to dismiss the small one.
    if (isSubmittingRef.current || isNestedDialogOpen) {
      return;
    }

    isSubmittingRef.current = true;
    setProcessAction(ProcessAction.cancel);

    for await (const paramDefinition of [locationPhotoParam, detailPhotoParam]) {
      if (!paramDefinition) {
        continue;
      }

      const paramAssetsForSave = assetsForSave?.[paramDefinition.id];
      if (!paramAssetsForSave?.length) {
        continue;
      }

      const imageIdsForDelete = paramAssetsForSave.map((asset) => asset.imageId);

      await deleteAssetsForParam(paramDefinition.id, compact(imageIdsForDelete));
      deleteAssetsOfVehicleCreate(compact(imageIdsForDelete));
    }

    setProcessAction(null);
    isSubmittingRef.current = false;
    onClose();
  };

  const handleDamageTypeChange = (chipValue: string[] | Nullish) => {
    const [value] = chipValue ?? [];
    setDamageType(value ?? null);
  };

  const handleDeleteDamage = async (): Promise<void> => {
    // Same in-flight-Save guard as `handleCancelDamage` — otherwise a stale Save success handler
    // could resurrect the damage this deletes.
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setProcessAction(ProcessAction.delete);

    deleteDamageValue(typeFieldName, typeOfDamageParam?.id);
    deleteDamageValue(commentFieldName, commentOfDamageParam?.id);

    const imageIds: string[] = [];
    [...locationAssets, ...detailAssets]?.forEach((file) => {
      const meta = getParsedValue(file?.meta);
      if (meta?.damageId !== damageId) {
        return;
      }
      if (file.imageId !== null) {
        imageIds.push(file.imageId);
      }
    });

    if (imageIds.length) {
      for await (const paramDefinition of [locationPhotoParam, detailPhotoParam]) {
        if (!paramDefinition) {
          continue;
        }

        dispatch(
          deleteAssets({
            categoryId: category.id,
            paramDefinitionId: paramDefinition.id,
            imageIds,
          })
        );

        deleteAssetsOfVehicleCreate(imageIds);
      }
    }

    setProcessAction(null);
    isSubmittingRef.current = false;
    setIsDeleteDialogOpen(false);
    onClose();

    if (imageIds.length) {
      await dispatch(deleteAuditAssets.action({requestBody: {imageIds}}));
    }
  };

  const handleDeleteImage = (paramDefinitionId: string, imageId: string) => {
    if (isEdit) {
      addAssetForDelete(paramDefinitionId, imageId);
      return;
    }

    deleteAssetForSave(paramDefinitionId, imageId);
  };
  const getCardUploadProps = (paramDefinition: AuditParamDefinition): CardImageUploadProps => ({
    paramDefinitionId: paramDefinition.id,
    categoryId: category.id,
    damageId,
    softDelete: isEdit,
    onUpload: (asset: AuditDataAssetsFilesBody) => {
      addAssetForSave(paramDefinition.id, asset);
    },
    onDelete: (imageId: string) => {
      handleDeleteImage(paramDefinition.id, imageId);
    },
  });

  return (
    <>
      <FormField
        data-testid={suffixTestId(`addAndEditDamage-${typeFieldName}`, rest)}
        name={typeFieldName}
        component={() => <></>}
      />
      <FormField
        data-testid={suffixTestId(`addAndEditDamage-${commentFieldName}`, rest)}
        name={commentFieldName}
        component={() => <></>}
      />

      <Dialog
        isOpen={isOpen}
        onClose={handleCancelDamage}
        title={getTranslation(locale, category.name)}
        data-testid={suffixTestId('addAndEditDamage', rest)}
      >
        <Show when={canChangePart}>
          <Label>{i18n.t('general.labels.location')}</Label>
          <Space vertical={1} />
          <Choice<string>
            value={resolvedTargetCategory.id}
            options={targetCategories.map((target) => ({
              value: target.id,
              label: getTranslation(locale, target.name),
            }))}
            onChange={handlePartChange}
            isNotClearable
            data-testid={suffixTestId('addAndEditDamage-part', rest)}
          />
          <Space vertical={4} />
        </Show>

        <Grid columns={1}>
          <Show when={targetTypeOfDamageParam}>
            <Chips
              label={i18n.t('entity.condition.labels.typeOfDamage')}
              isRequired
              isInvalid={isDamageTypeInvalid}
              errorMessage={
                isDamageTypeInvalid ? i18n.t('general.validations.fieldIsRequired') : undefined
              }
              value={damageType ? [damageType] : []}
              options={
                targetTypeOfDamageParam?.values.map((paramValue) => ({
                  ...paramValue,
                  label: paramValue.label ? getTranslation(locale, paramValue.label) : '',
                })) ?? []
              }
              onChange={handleDamageTypeChange}
              data-testid={suffixTestId('addAndEditDamage-typeOfDamage', rest)}
            />
          </Show>

          <GridItem>
            <Grid columns={6}>
              <Show when={locationPhotoParam}>
                <GridItem span={1}>
                  <Label>{i18n.t('general.labels.location')}</Label>
                  <CardImageUpload
                    ratio="16 / 10"
                    {...getCardUploadProps(locationPhotoParam)}
                    data-testid={suffixTestId('addAndEditDamage-location', rest)}
                  />
                </GridItem>
              </Show>
              <Show when={detailPhotoParam}>
                <GridItem span={5}>
                  <Label>{i18n.t('general.labels.detail')}</Label>
                  <CardImageUpload
                    {...getCardUploadProps(detailPhotoParam)}
                    columns={5}
                    ratio="16 / 10"
                    isAlwaysShowUpload
                    multiple
                    data-testid={suffixTestId('addAndEditDamage-detail', rest)}
                  />
                </GridItem>
              </Show>
            </Grid>
          </GridItem>

          <GridItem>
            <Label>{i18n.t('general.labels.note')}</Label>
            <Textarea
              value={damageComment ?? ''}
              minRows={1}
              onChange={(value) => setDamageComment(value)}
              data-testid={suffixTestId('addAndEditDamage-note', rest)}
            />
          </GridItem>
          <Show when={isEdit}>
            <Right>
              <Button
                variant="dangerGhost"
                onClick={() => setIsDeleteDialogOpen(true)}
                leftIcon="action/delete"
                isDisabled={isNotNil(processAction)}
                data-testid={suffixTestId('addAndEditDamage-deleteAction', rest)}
                title={i18n.t('general.actions.delete')}
              />
            </Right>
          </Show>
        </Grid>

        <Dialog
          size="small"
          isOpen={isNotNil(pendingTargetCategoryId)}
          onClose={() => setPendingTargetCategoryId(null)}
          title={i18n.t('entity.condition.labels.damageTypeUnsupportedByPartDialogTitle')}
          data-testid={suffixTestId('addAndEditDamage-partChangeDamageTypeReset', rest)}
        >
          <Text>{i18n.t('entity.condition.labels.damageTypeUnsupportedByPartDialogText')}</Text>
          <Space vertical={4} />
          <ButtonGroup align="right">
            <Button
              variant="secondary"
              onClick={() => setPendingTargetCategoryId(null)}
              data-testid={suffixTestId('addAndEditDamage-partChangeDamageTypeReset-cancel', rest)}
              title={i18n.t('general.actions.cancel')}
            />
            <Button
              variant="primary"
              onClick={handleConfirmPartChange}
              data-testid={suffixTestId('addAndEditDamage-partChangeDamageTypeReset-confirm', rest)}
              title={i18n.t('general.actions.confirm')}
            />
          </ButtonGroup>
        </Dialog>

        <Dialog
          size="small"
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          title={i18n.t('entity.condition.labels.damageDeleteConfirmationDialogText')}
          data-testid={suffixTestId('addAndEditDamage-conditionTypeDamageDeleteConfirmation', rest)}
        >
          <ButtonGroup align="right">
            <Button
              onClick={() => setIsDeleteDialogOpen(false)}
              variant="secondary"
              isDisabled={processAction === ProcessAction.delete}
              data-testid={suffixTestId(
                'addAndEditDamage-conditionTypeDamageDeleteConfirmation-cancel',
                rest
              )}
              title={i18n.t('general.actions.cancel')}
            />
            <Button
              variant="danger"
              onClick={handleDeleteDamage}
              isDisabled={isNotNil(processAction) && processAction !== ProcessAction.delete}
              isLoading={processAction === ProcessAction.delete}
              data-testid={suffixTestId(
                'addAndEditDamage-conditionTypeDamageDeleteConfirmation-delete',
                rest
              )}
              title={i18n.t('general.actions.delete')}
            />
          </ButtonGroup>
        </Dialog>

        <Space vertical={4} />
        <ButtonGroup
          align="right"
          data-testid={suffixTestId(
            `addAndEditDamage-conditionTypeDamageDeleteConfirmation-actions`,
            rest
          )}
        >
          <Button
            variant="secondary"
            onClick={handleCancelDamage}
            isDisabled={processAction === ProcessAction.confirm}
            isLoading={processAction === ProcessAction.cancel}
            data-testid={suffixTestId(
              `addAndEditDamage-conditionTypeDamageDeleteConfirmation-${
                isEdit ? 'cancel' : 'discard'
              }`,
              rest
            )}
            title={isEdit ? i18n.t('general.actions.cancel') : i18n.t('general.actions.discard')}
          />
          <Button
            variant="primary"
            onClick={handleConfirmDamage}
            isDisabled={isNotNil(processAction)}
            isLoading={processAction === ProcessAction.confirm}
            data-testid={suffixTestId(
              `addAndEditDamage-conditionTypeDamageDeleteConfirmation-${
                isEdit ? 'saveDamage' : 'addDamage'
              }`,
              rest
            )}
            title={
              isEdit
                ? i18n.t('entity.condition.actions.saveDamage')
                : i18n.t('entity.condition.actions.addDamage')
            }
          />
        </ButtonGroup>
      </Dialog>
    </>
  );
};
